import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient, withClientId } from '@/lib/queryClient'

import {
  createTemplate,
  deleteTemplate,
  fetchConversation,
  fetchConversations,
  fetchTemplates,
  sendMessage,
  updateTemplate,
} from './api'
import type { Message, SendMessageBody, TemplateWriteBody } from '@/types/messaging'

export const MESSAGING_KEYS = {
  conversations: ['conversations'] as const,
  conversation: (customerId: string) => ['messages', customerId] as const,
  templates: ['message-templates'] as const,
}

function invalidateMessageData(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: MESSAGING_KEYS.conversations })
  qc.invalidateQueries({ queryKey: ['messages'] })
}

export const useConversations = () =>
  useQuery({
    queryKey: MESSAGING_KEYS.conversations,
    queryFn: fetchConversations,
  })

// Polls so customer replies (arriving via the Twilio webhook) show up while
// the thread is open.
export const useConversation = (customerId: string, poll = true) =>
  useQuery({
    queryKey: MESSAGING_KEYS.conversation(customerId),
    queryFn: () => fetchConversation(customerId),
    enabled: !!customerId,
    ...(poll ? { refetchInterval: 30_000 } : {}),
  })

// Outbox keys (see apps/frontend/AGENTS.md "offline outbox"): paused offline
// mutations replay through the setMutationDefaults registrations below. The
// send carries a client-generated id so a replay can never double-text the
// customer — the backend short-circuits before Twilio on an id conflict.
export const MESSAGE_SEND_KEY = ['outbox', 'messages', 'send'] as const
export const TEMPLATE_CREATE_KEY = ['outbox', 'message-templates', 'create'] as const
export const TEMPLATE_UPDATE_KEY = ['outbox', 'message-templates', 'update'] as const
export const TEMPLATE_DELETE_KEY = ['outbox', 'message-templates', 'delete'] as const

queryClient.setMutationDefaults(MESSAGE_SEND_KEY, { mutationFn: sendMessage })
queryClient.setMutationDefaults(TEMPLATE_CREATE_KEY, { mutationFn: createTemplate })
queryClient.setMutationDefaults(TEMPLATE_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: TemplateWriteBody }) =>
    updateTemplate(vars.id, vars.body),
})
queryClient.setMutationDefaults(TEMPLATE_DELETE_KEY, { mutationFn: deleteTemplate })

type SendContext = { key: readonly unknown[]; previous: Message[] } | undefined

export const useSendMessage = () => {
  const qc = useQueryClient()
  const mutation = useMutation<Message, Error, SendMessageBody & { id: string }, SendContext>({
    mutationKey: MESSAGE_SEND_KEY,
    mutationFn: sendMessage,
    // Show the outbound message in its thread immediately — including while
    // it sits paused in the offline queue (status stays 'queued' until the
    // replay succeeds and the invalidation refetches server truth).
    onMutate: (vars) => {
      if (!vars.customer_id) return undefined
      const key = MESSAGING_KEYS.conversation(vars.customer_id)
      const existing = qc.getQueryData<Message[]>(key)
      if (!existing) return undefined
      const optimistic: Message = {
        id: vars.id,
        customer_id: vars.customer_id,
        direction: 'outbound',
        status: 'queued',
        body: vars.body ?? '',
        template_id: vars.template_id ?? null,
        deal_id: vars.deal_id ?? null,
        work_order_id: vars.work_order_id ?? null,
        to_number: '',
        twilio_sid: '',
        created_at: new Date().toISOString(),
        sent_at: null,
      }
      qc.setQueryData<Message[]>(key, [...existing, optimistic])
      return { key, previous: existing }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) qc.setQueryData(ctx.key, ctx.previous)
    },
    onSuccess: (message) => {
      invalidateMessageData(qc)
      qc.invalidateQueries({ queryKey: MESSAGING_KEYS.conversation(message.customer_id) })
    },
  })
  return {
    ...mutation,
    mutate: (body: SendMessageBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: SendMessageBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  }
}

export const useTemplates = () =>
  useQuery({
    queryKey: MESSAGING_KEYS.templates,
    queryFn: fetchTemplates,
  })

export const useCreateTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: TEMPLATE_CREATE_KEY,
    mutationFn: (body: TemplateWriteBody) => createTemplate(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: MESSAGING_KEYS.templates }),
  })
}

export const useUpdateTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: TEMPLATE_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: TemplateWriteBody }) =>
      updateTemplate(vars.id, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: MESSAGING_KEYS.templates }),
  })
}

export const useDeleteTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: TEMPLATE_DELETE_KEY,
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: MESSAGING_KEYS.templates }),
  })
}
